<template>
  <v-card
    class="mx-auto pa-0 card-standard standard-row"
    :class="[
      stackClass,
      stackModeClass,
      bodyStackClass,
      densityClass,
      emphasisClass,
      cardMarginClass,
    ]"
    :elevation="elev"
  >
    <v-card-title
      class="ma-0 pa-0 standard-row__surface"
      :class="[surfaceClass, { 'standard-row__title--flush': flushPadding }]"
    >
      <v-container class="pa-0 standard-row__container" fluid>
        <v-row
          :no-gutters="true"
          class="d-flex justify-start ma-0 standard-row__layout"
        >
          <v-col
            v-if="firstColumnSide == 'left'"
            :cols="firstColumnWidth"
            class="standard-row__cell standard-row__cell--first"
            :class="[firstColumnClassList, { 'text-truncate': !wrapFirstColumn }]"
          >
            <slot name="firstColumn">
              <!-- insert components -->
            </slot>
          </v-col>

          <!-- Remaining columns -->
          <v-col
            :cols="rColWidth"
            class="standard-row__cell standard-row__cell--remaining"
            :class="[
              remainingColumnClassList,
              { 'text-truncate': !wrapRemainingColumns },
            ]"
          >
            <!-- When using data passed in -->
            <v-row
              v-if="useData"
              :no-gutters="true"
              class="d-flex ma-0 standard-row__inner-layout"
              :class="remainingLayoutClass"
            >
              <v-col
                v-for="(d, i) in data"
                :key="i"
                class="ma-0 pa-0 standard-row__cell standard-row__cell--data"
                :class="remainingDataCellClassList"
              >
                <p v-if="item == 'none'">{{ d }}</p>
                <p v-else>{{ d[item] }}</p>
              </v-col>
            </v-row>

            <!-- When using data inserted into slot -->
            <v-row
              v-if="!useData"
              :no-gutters="true"
              class="d-flex ma-0 standard-row__inner-layout"
              :class="remainingLayoutClass"
            >
              <slot name="remainingColumns">
                <!-- insert components -->
              </slot>
            </v-row>
          </v-col>

          <!-- Used when first column is moved to right side -->
          <v-col
            v-if="firstColumnSide == 'right'"
            :cols="firstColumnWidth"
            class="standard-row__cell standard-row__cell--first standard-row__cell--right ma-0"
            :class="[firstColumnClassList, { 'text-truncate': !wrapFirstColumn }]"
          >
            <slot name="firstColumn">
              <!-- insert components -->
            </slot>
          </v-col>
        </v-row>
      </v-container>
    </v-card-title>
  </v-card>
</template>

<script setup>
  import { computed } from "vue";

  const props = defineProps({
    rowLevel: { type: Number, default: 1 },
    firstColHighlighted: { type: Boolean, default: false },
    compactLayout: { type: Boolean, default: false },
    borderRight: { type: Boolean, default: false },
    borderTop: { type: Boolean, default: false },
    firstColumnWidth: { type: Number, default: 2 },
    firstColumnSide: { type: String, default: "left" },
    cardMargin: { type: String, default: "" },
    elev: { type: String, default: "0" },
    useData: { type: Boolean, default: false },
    data: { type: Array, default: null },
    item: { type: String, default: null },
    flushPadding: { type: Boolean, default: false },
    density: { type: String, default: "default" },
    stack: { type: String, default: "solo" },
    emphasis: { type: String, default: "default" },
    firstColumnTextRole: { type: String, default: "row-label" },
    remainingColumnsTextRole: { type: String, default: "body" },
    firstColumnAlign: { type: String, default: "left" },
    remainingColumnsAlign: { type: String, default: "center" },
    wrapFirstColumn: { type: Boolean, default: false },
    wrapRemainingColumns: { type: Boolean, default: false },
  });

  const rColWidth = computed(() =>
    props.firstColumnSide === "none" ? 12 : 12 - props.firstColumnWidth
  );

  const surfaceClass = computed(() => {
    const levels = {
      1: "cardBodySection01",
      2: "cardBodySection02",
      3: "cardBodySection03",
      4: "cardBodySection04",
    };

    return levels[props.rowLevel] || "cardBodySection01";
  });

  function textRoleClass(role) {
    const roleMap = {
      "page-title": "ui-text-page-title",
      "section-title": "ui-text-section-title",
      "row-label": "ui-text-row-label",
      body: "ui-text-body",
      meta: "ui-text-meta",
      value: "ui-text-value",
      helper: "ui-text-helper",
      error: "ui-text-error",
    };

    return roleMap[role] || "ui-text-body";
  }

  function alignClass(align) {
    const alignMap = {
      left: "standard-row__cell--align-left",
      center: "standard-row__cell--align-center",
      right: "standard-row__cell--align-right",
    };

    return alignMap[align] || "standard-row__cell--align-left";
  }

  const densityClass = computed(() => `standard-row--density-${props.density}`);
  const stackClass = computed(() => `standard-row--stack-${props.stack}`);
  const stackModeClass = computed(() =>
    props.stack.startsWith("body-")
      ? `standard-row--stack-${props.stack.replace("body-", "")}`
      : null
  );
  const bodyStackClass = computed(() =>
    props.stack.startsWith("body-") ? "standard-row--body-group" : null
  );
  const emphasisClass = computed(
    () => `standard-row--emphasis-${props.emphasis}`
  );
  const cardMarginClass = computed(() => props.cardMargin || "");
  const firstColumnClassList = computed(() => [
    textRoleClass(props.firstColumnTextRole),
    alignClass(props.firstColumnAlign),
    {
      borderRight: props.borderRight,
      borderTop: props.borderTop,
      "standard-row__cell--highlighted": props.firstColHighlighted,
      "standard-row__cell--wrap": props.wrapFirstColumn,
    },
  ]);
  const remainingColumnClassList = computed(() => [
    textRoleClass(props.remainingColumnsTextRole),
    alignClass(props.remainingColumnsAlign),
    {
      borderRight: props.borderRight,
      borderTop: props.borderTop,
    },
  ]);
  const remainingDataCellClassList = computed(() => [
    textRoleClass(props.remainingColumnsTextRole),
    alignClass(props.remainingColumnsAlign),
    {
      borderRight: props.borderRight,
      borderTop: props.borderTop,
    },
  ]);
  const remainingLayoutClass = computed(
    () => `standard-row__inner-layout--${props.remainingColumnsAlign}`
  );
</script>

<style lang="scss" scoped>
  .standard-row {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    margin-block: var(--cssStandardRowGap);
  }

  .standard-row__title--flush {
    padding: 0 !important;
  }

  .standard-row__surface {
    --cssSurfaceRadiusTop: var(--cssStandardRowRadius);
    --cssSurfaceRadiusBottom: var(--cssStandardRowRadius);
    --cssSurfacePadY: var(--cssStandardRowPadY);
    --cssSurfacePadX: var(--cssStandardRowPadX);
    --cssSurfaceMinHeight: var(--cssStandardRowMinHeight);
    min-height: var(--cssSurfaceMinHeight);
  }

  .standard-row__container,
  .standard-row__layout,
  .standard-row__inner-layout {
    min-height: inherit;
  }

  .standard-row__layout {
    align-items: stretch;
  }

  .standard-row__cell {
    min-height: inherit;
    display: flex;
    align-items: center;
    align-self: stretch;
    padding: 0 var(--cssCellPadX);
  }

  .standard-row__cell--first {
    padding-left: var(--cssStandardRowPadX);
    padding-right: var(--cssCellPadX);
  }

  .standard-row__cell--remaining {
    padding-inline: 0;
  }

  .standard-row__cell--data {
    justify-content: center;
    padding-inline: var(--cssCellPadX);
  }

  .standard-row__cell--right {
    justify-content: flex-end;
    padding-right: var(--cssCellPadX);
  }

  .standard-row__cell--align-left {
    justify-content: flex-start;
    text-align: left;
  }

  .standard-row__cell--align-center {
    justify-content: center;
    text-align: center;
  }

  .standard-row__cell--align-right {
    justify-content: flex-end;
    text-align: right;
  }

  .standard-row__cell--wrap {
    overflow: visible;
    text-overflow: clip;
    white-space: normal !important;
  }

  .standard-row__inner-layout--left {
    justify-content: flex-start;
  }

  .standard-row__inner-layout--center {
    justify-content: center;
  }

  .standard-row__inner-layout--right {
    justify-content: flex-end;
  }

  .standard-row__cell--highlighted {
    background-color: var(--cssBodySection-2);
  }

  .standard-row--density-compact {
    margin-block: var(--cssStandardRowGapTight);
  }

  .standard-row--density-compact .standard-row__surface {
    --cssSurfaceMinHeight: var(--cssStandardRowMinHeightCompact);
    --cssSurfacePadY: var(--cssStandardRowPadYCompact);
  }

  .standard-row--density-dense {
    margin-block: var(--cssStandardRowGapTight);
  }

  .standard-row--density-dense .standard-row__surface {
    --cssSurfaceMinHeight: var(--cssStandardRowMinHeightDense);
    --cssSurfacePadY: var(--cssStandardRowPadYDense);
  }

  .standard-row--density-info {
    margin-block: 0;
  }

  .standard-row--density-info .standard-row__surface {
    --cssSurfaceMinHeight: 1.65rem;
    --cssSurfacePadY: 0.08rem;
  }

  .standard-row--density-info .standard-row__cell {
    min-height: var(--cssSurfaceMinHeight);
  }

  .standard-row--stack-start,
  .standard-row--stack-middle,
  .standard-row--stack-end,
  .standard-row--stack-flush {
    margin-block: 0;
  }

  .standard-row--stack-start {
    margin-top: var(--cssStandardRowGapTight);
  }

  .standard-row--stack-end {
    margin-bottom: var(--cssStandardRowGapTight);
  }

  .standard-row--stack-start .standard-row__surface {
    --cssSurfaceRadiusBottom: 0;
  }

  .standard-row--stack-middle .standard-row__surface {
    --cssSurfaceRadiusTop: 0;
    --cssSurfaceRadiusBottom: 0;
  }

  .standard-row--stack-end .standard-row__surface {
    --cssSurfaceRadiusTop: 0;
  }

  .standard-row--stack-middle .standard-row__surface,
  .standard-row--stack-end .standard-row__surface {
    border-top: 1px solid var(--cssSurfaceBorder);
  }

  .standard-row--stack-flush .standard-row__surface {
    --cssSurfaceRadiusTop: 0;
    --cssSurfaceRadiusBottom: 0;
  }

  .standard-row--body-group {
    margin-block: 0;
  }

  .standard-row--body-group .standard-row__surface {
    --cssSurfaceRadiusTop: 0;
    --cssSurfaceRadiusBottom: 0;
  }

  .standard-row--body-group,
  .standard-row--body-group .standard-row__surface {
    border-radius: 0 !important;
  }

  .standard-row--body-group.standard-row--stack-middle .standard-row__surface,
  .standard-row--body-group.standard-row--stack-end .standard-row__surface {
    border-top: 1px solid var(--cssSurfaceBorder);
  }

  .standard-row--body-group .standard-row__surface,
  .standard-row--body-group .standard-row__container,
  .standard-row--body-group .standard-row__layout,
  .standard-row--body-group .standard-row__cell {
    background-clip: padding-box;
  }

  .standard-row--body-group .standard-row__cell--first {
    padding-left: var(--cssStandardRowPadX);
    padding-right: var(--cssCellPadX);
  }

  .standard-row--body-group .standard-row__inner-layout {
    align-items: stretch;
  }

  .standard-row--body-group :deep(.standard-row__inner-layout > .v-col),
  .standard-row--body-group :deep(.standard-row__inner-layout > [class*="v-col"]) {
    display: flex;
    align-items: center;
    align-self: stretch;
    min-height: inherit;
  }

  .standard-row--body-group :deep(.standard-row__inner-layout > .v-col > .d-flex),
  .standard-row--body-group :deep(.standard-row__inner-layout > [class*="v-col"] > .d-flex) {
    align-items: center;
    min-height: 100%;
    width: 100%;
  }

  .standard-row--body-group :deep(.standard-row__inner-layout > .v-col > .d-flex > *:not(:last-child)),
  .standard-row--body-group :deep(.standard-row__inner-layout > [class*="v-col"] > .d-flex > *:not(:last-child)) {
    border-right: 1px solid var(--cssBodyRowInternalDivider);
  }

  .standard-row--body-group :deep(.isEditable),
  .standard-row--body-group :deep(.isSingleEditable),
  .standard-row--body-group :deep(.isSingleEditableNegative),
  .standard-row--body-group :deep(.isSingleEditablePosNeg) {
    align-self: stretch;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 100%;
  }

  .standard-row--body-group :deep(input),
  .standard-row--body-group :deep(label),
  .standard-row--body-group :deep(button) {
    align-self: stretch;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .standard-row--body-group :deep(.borderTop) {
    border-top: 0 !important;
  }

  .standard-row--emphasis-subtle .standard-row__surface {
    border: 1px solid var(--cssSurfaceBorderSoft);
  }

  .standard-row--emphasis-strong .standard-row__surface {
    border: 1px solid var(--cssSurfaceBorderStrong);
  }
</style>
